import {
  Circle, Eraser, Hand, Hexagon, Map as MapIcon, MousePointer2, PenTool, Route, Ruler, Squircle, Stamp, Type,
  SquareDashedMousePointer,
} from "lucide-react";
import { useT } from "@/src/i18n";
import { useMapStore } from "@/src/store/useMapStore";
import type { TranslationKey } from "@/src/i18n";
import type { ActiveTool, MapMode } from "@/src/types/map";

interface ToolDefinition {
  id: ActiveTool;
  labelKey: TranslationKey;
  keyLabel: string;
  icon: typeof Hand;
  group: "navigate" | "build" | "world" | "annotate";
  modes?: MapMode[];
}

const tools: ToolDefinition[] = [
  { id: "select", labelKey: "tool.select", keyLabel: "V", icon: MousePointer2, group: "navigate" },
  { id: "pan", labelKey: "tool.pan", keyLabel: "H", icon: Hand, group: "navigate" },
  { id: "measure", labelKey: "tool.measure", keyLabel: "M", icon: Ruler, group: "navigate" },
  { id: "room", labelKey: "tool.room", keyLabel: "R", icon: SquareDashedMousePointer, group: "build", modes: ["square"] },
  { id: "ellipse", labelKey: "tool.ellipse", keyLabel: "O", icon: Circle, group: "build", modes: ["square"] },
  { id: "hexagon", labelKey: "tool.hexagon", keyLabel: "X", icon: Hexagon, group: "build", modes: ["square"] },
  { id: "corridor", labelKey: "tool.corridor", keyLabel: "C", icon: Route, group: "build", modes: ["square"] },
  { id: "polygon", labelKey: "tool.polygon", keyLabel: "G", icon: PenTool, group: "build", modes: ["square"] },
  { id: "erase", labelKey: "tool.erase", keyLabel: "E", icon: Eraser, group: "build", modes: ["square"] },
  { id: "patch", labelKey: "tool.patch", keyLabel: "P", icon: Squircle, group: "world", modes: ["square"] },
  { id: "terrain", labelKey: "tool.terrain", keyLabel: "T", icon: MapIcon, group: "world", modes: ["hex"] },
  { id: "label", labelKey: "tool.label", keyLabel: "L", icon: Type, group: "annotate" },
  { id: "stamp", labelKey: "tool.stamp", keyLabel: "S", icon: Stamp, group: "annotate" },
];

export function Toolbar() {
  const activeTool = useMapStore((state) => state.tool);
  const mode = useMapStore((state) => state.meta.gridType);
  const setTool = useMapStore((state) => state.setTool);
  const t = useT();
  const visibleTools = tools.filter((tool) => !tool.modes || tool.modes.includes(mode));
  return (
    <nav
      className="flex max-w-[calc(100vw-24px)] items-center gap-1 overflow-x-auto rounded-2xl border border-white/10 bg-[#1c1b18]/95 p-1.5 shadow-2xl shadow-black/25 backdrop-blur md:max-h-[calc(100vh-150px)] md:w-[68px] md:flex-col md:overflow-y-auto md:overflow-x-hidden"
      aria-label={t("toolbar.label")}
    >
      {visibleTools.map(({ id, labelKey, keyLabel, icon: Icon, group }, index) => {
        const active = activeTool === id;
        const label = t(labelKey);
        const previousGroup = visibleTools[index - 1]?.group;
        return (
          <div key={id} className="contents">
            {index > 0 && previousGroup !== group && <span className="mx-1 h-7 w-px shrink-0 bg-white/10 md:my-1 md:h-px md:w-9" />}
            <button
              type="button"
              onClick={() => setTool(id)}
              className={`group relative flex h-11 min-w-14 shrink-0 items-center justify-center rounded-xl px-2 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d85b4f] md:w-14 md:flex-col md:px-0 ${active ? "bg-[#b4362f] text-white shadow-lg shadow-[#8e2520]/25" : "text-stone-400 hover:bg-white/7 hover:text-white"}`}
              aria-pressed={active}
              aria-label={t("tool.aria", { label, key: keyLabel })}
              title={t("tool.title", { label, key: keyLabel })}
            >
              <Icon size={18} strokeWidth={1.8} />
              <span className="ms-1.5 text-[10px] font-semibold md:ms-0 md:mt-0.5">{label}</span>
            </button>
          </div>
        );
      })}
    </nav>
  );
}
